import { has } from 'lodash-es';
import type { FieldError } from 'react-hook-form';
import type { SystemPackageVersionAutocompleteFieldValue } from '../../components/system-package-version-fields/systemPackageVersionFields.model';
import type { ArtifactComponentTypeEnum } from '../../domain/models/artifact.model';
import type { SystemPackageArtifactType } from '../../domain/models/systemPackage.model';

export interface UseCreateSystemPackageVersionPageVMOutput {
  readonly title: string;
  readonly parentEntityId?: string;
}

//======== define version
export interface AppVersionDefinition {
  readonly version: string; //pattern
  readonly releaseNotes?: string;
  readonly operationalSystemDependency?: string;
  readonly minimumOSVersion?: SystemPackageVersionAutocompleteFieldValue[];
  readonly systemPackageDependency?: string;
  readonly minimumSystemPackageVersion?: SystemPackageVersionAutocompleteFieldValue[];
  readonly appDependency?: string;
  readonly minimumAppVersion?: SystemPackageVersionAutocompleteFieldValue[];
}

export interface SystemPackageVersionDownloadPolicy {
  readonly name: string;
  readonly version?: string;
}

export interface AppVersionApplicationFile {
  readonly checksum: string;
  readonly componentType: Extract<ArtifactComponentTypeEnum, 'APPLICATION' | 'OS' | 'SYSTEM_PACKAGE'>;
  readonly error?: boolean;
  readonly extension?: string;
  readonly file?: File;
  readonly id?: string;
  readonly messageError?: string;
  readonly name: string;
  readonly order?: number;
  readonly parsedTemplateId?: string;
  readonly size: number;
  readonly targetLocation?: string;
  readonly templateFileName?: string;
  readonly templateFileVersion?: string;
  readonly type?: SystemPackageArtifactType;
  readonly uploadId?: string;
  readonly uploadStatus?: AppVersionFileUploadErrorStatus;
  readonly downloadPolicy?: SystemPackageVersionDownloadPolicy[];
}

//======== build

export interface AppVersionExistingAppFiles extends SystemPackageVersionAutocompleteFieldValue {
  readonly order?: number;
  readonly downloadPolicy?: SystemPackageVersionDownloadPolicy[];
}

export interface AppVersionBuild {
  readonly existingAppFiles: AppVersionExistingAppFiles[];
  readonly applicationFiles: ParameterTemplate[];
}

export function isParameterTemplate(
  value: AppVersionApplicationFile | AppVersionExistingAppFiles | ParameterTemplate,
): value is ParameterTemplate {
  return has(value, 'template') && has(value, 'targetLocation');
}
export function isArrayOfParameterTemplate(
  value: AppVersionApplicationFile[] | AppVersionExistingAppFiles[] | ParameterTemplate[],
): value is ParameterTemplate[] {
  return value.every((el) => isParameterTemplate(el));
}

export function isAppVersionExistingAppFiles(
  value: AppVersionApplicationFile | AppVersionExistingAppFiles | ParameterTemplate,
): value is AppVersionExistingAppFiles {
  return has(value, 'value') && has(value, 'label');
}

//======== configure
export interface ParameterTemplate {
  readonly targetLocation: string;
  readonly template: AppVersionApplicationFile[];
  readonly id: string;
}

export interface AppVersionConfigure {
  readonly existingTemplates: SystemPackageVersionAutocompleteFieldValue[];
  readonly parameterTemplates: ParameterTemplate[];
}

export interface SystemPackageVersionParams {
  readonly parentEntityId?: string;
  readonly redirectRouteOnCreate?: string;
}

export interface SystemPackageAppVersion {
  readonly definition: AppVersionDefinition;
  readonly build: AppVersionBuild;
  readonly configure: AppVersionConfigure;
}

export interface TargetLocationError {
  readonly targetLocation: FieldError;
}

export enum AppVersionFileUploadError {
  UploadFileError = 'UPLOAD_FILE_ERROR',
}

export enum AppVersionFileUploadErrorStatus {
  None = 'NONE',
  Failure = 'FAILURE',
  Success = 'SUCCESS',
}

export interface OnPartialUploadParams {
  readonly fulfilled: Record<string, AppVersionApplicationFile>;
  readonly rejected: Record<string, AppVersionApplicationFile>;
}
